<template>
    <!-- Admin: add or edit one FAQ. Write on the left, the customer's view on the right. -->
    <v-dialog :model-value="modelValue" @update:model-value="emit('update:modelValue', $event)" max-width="1200" scrollable>
        <div class="faq-editor">
            <div class="faq-editor__head">
                <h2>{{ item && item.id ? 'Edit question' : 'New question' }}</h2>
                <button type="button" class="faq-editor__x" aria-label="Close" @click="emit('update:modelValue', false)">×</button>
            </div>
            <div class="faq-editor__body">
                <label class="faq-editor__label">Question</label>
                <input v-model="question" class="faq-editor__input" placeholder="e.g. Can I change my proxy location?" />
                <div class="faq-editor__cols">
                    <div class="faq-editor__write">
                        <div class="faq-editor__label-row">
                            <label class="faq-editor__label">Answer</label>
                            <button type="button" class="faq-editor__link" @click="showHelp = !showHelp">{{ showHelp ? 'Hide' : 'Formatting guide' }}</button>
                        </div>
                        <div class="faq-editor__tools" role="toolbar" aria-label="Formatting">
                            <button v-for="tool in TOOLS" :key="tool.label" type="button" :title="tool.title" @click="apply(tool)">{{ tool.label }}</button>
                        </div>
                        <textarea ref="area" v-model="answer" class="faq-editor__area" spellcheck="true"></textarea>
                        <div v-if="showHelp" class="faq-editor__help">
                            <div v-for="h in HELP" :key="h[0]"><code>{{ h[0] }}</code><span>{{ h[1] }}</span></div>
                        </div>
                    </div>
                    <div class="faq-editor__preview">
                        <label class="faq-editor__label">Preview</label>
                        <div class="faq-editor__paper">
                            <p class="faq-editor__pq">{{ question || 'Your question' }}</p>
                            <div class="faq-prose" v-html="preview.html"></div>
                        </div>
                    </div>
                </div>
            </div>
            <div class="faq-editor__foot">
                <span class="faq-editor__meta">{{ preview.words }} words<template v-if="preview.toc.length"> · {{ preview.toc.length }} sections</template></span>
                <button type="button" class="faq-btn" @click="emit('update:modelValue', false)">Cancel</button>
                <button type="button" class="faq-btn faq-btn--main" :disabled="saving || !question.trim() || !answer.trim()" @click="save">{{ saving ? 'Saving…' : 'Save' }}</button>
            </div>
        </div>
    </v-dialog>
</template>

<script setup>
import { ref, computed, watch, nextTick, defineProps, defineEmits } from 'vue';
import { renderFaq } from '@/helpers/faq-format';

const props = defineProps({ modelValue: Boolean, item: { type: Object, default: null } });
const emit = defineEmits(['update:modelValue', 'save']);

const question = ref('');
const answer = ref('');
const saving = ref(false);
const showHelp = ref(false);
const area = ref(null);

watch(() => [props.modelValue, props.item], () => {
    if (!props.modelValue) return;
    question.value = props.item?.question || '';
    answer.value = props.item?.answer || '';
    saving.value = false;
}, { immediate: true });

const preview = computed(() => renderFaq(answer.value, 'preview'));

// wrap the selection, or insert a block on its own lines
const TOOLS = [
    { label: 'B', title: 'Bold', wrap: ['**', '**'], sample: 'bold text' },
    { label: 'I', title: 'Italic', wrap: ['*', '*'], sample: 'italic text' },
    { label: 'H', title: 'Section heading (listed in "On this page")', line: '## ', sample: 'Section title' },
    { label: 'h', title: 'Smaller heading', line: '### ', sample: 'Smaller heading' },
    { label: '• List', title: 'Bullet list', line: '- ', sample: 'List item' },
    { label: '1. List', title: 'Numbered list', line: '1. ', sample: 'First step' },
    { label: '“ Quote', title: 'Quote', line: '> ', sample: 'Quoted text' },
    { label: 'Link', title: 'Link', wrap: ['[', '](https://)'], sample: 'link text' },
    { label: 'Yes/No', title: 'Coloured short answer: [yes], [no] or [maybe]', block: '[yes] Yes' },
    { label: 'Note', title: 'Highlighted warning box', block: ':::note\n**Please note:** text\n:::' },
    { label: 'Table', title: 'Table', block: '| Column | Column |\n| --- | --- |\n| Text | Text |' },
    { label: 'Steps', title: 'Numbered cards: Title | description', block: ':::steps\nFirst | What to do\nSecond | What to do next\n* Most important | Highlighted card\n:::' },
    { label: 'Compare', title: 'Two columns side by side', block: ':::compare\n### Option one | May mean\n- Point\n- Point\n### Option two | May involve\n- Point\n- Point\n:::' },
    { label: 'Formula', title: 'Centred line', block: ':::formula\nA + B + C\n:::' },
    { label: 'Q&A', title: 'Small expandable questions', block: ':::qa\n### A short question?\nIts answer.\n### Another question?\nIts answer.\n:::' },
];
const HELP = [
    ['## Title', 'Section heading, listed in "On this page"'], ['### Title', 'Smaller heading'],
    ['**bold**  *italic*', 'Bold and italic'], ['- item', 'Bullet list (6+ short items show in two columns)'],
    ['1. item', 'Numbered list'], ['> text', 'Quote'], ['[text](https://…)', 'Link'],
    ['[yes] / [no] / [maybe] text', 'Coloured short answer'], [':::note … :::', 'Warning box'],
    ['| a | b |', 'Table (second line | --- | --- |)'], [':::steps … :::', 'Cards, one per line: Title | description (* highlights)'],
    [':::compare … :::', '### Title | subtitle, then a list, twice'], [':::formula … :::', 'Centred line, + and ≠ highlighted'],
    [':::qa … :::', '### Question, then its answer'],
];
const apply = async (tool) => {
    const el = area.value; if (!el) return;
    const text = answer.value, s = el.selectionStart, e = el.selectionEnd;
    const selected = text.slice(s, e);
    let insert, cursor;
    if (tool.wrap) { const inner = selected || tool.sample; insert = tool.wrap[0] + inner + tool.wrap[1]; cursor = s + tool.wrap[0].length + inner.length; }
    else if (tool.line) {
        const body = (selected || tool.sample).split('\n').map((l) => tool.line + l.replace(/^(#{1,3} |- |\d+\. |> )/, '')).join('\n');
        const before = s > 0 && text[s - 1] !== '\n' ? '\n' : '';
        insert = before + body; cursor = s + insert.length;
    } else {
        const before = s > 0 && text[s - 1] !== '\n' ? '\n\n' : (s > 0 && text[s - 2] !== '\n' ? '\n' : '');
        insert = before + tool.block + '\n'; cursor = s + insert.length;
    }
    answer.value = text.slice(0, s) + insert + text.slice(e);
    await nextTick(); el.focus(); el.setSelectionRange(cursor, cursor);
};

const save = () => {
    saving.value = true;
    emit('save', { id: props.item?.id || null, question: question.value.trim(), answer: answer.value.replace(/\s+$/, '') });
};
</script>

<style scoped>
.faq-editor { display: flex; flex-direction: column; max-height: 92vh; background: var(--ui-card); color: var(--ui-ink); border: 1px solid var(--ui-line-strong); border-radius: 14px; overflow: hidden; }
.faq-editor__head { display: flex; align-items: center; justify-content: space-between; padding: 14px 18px; border-bottom: 1px solid var(--ui-line); }
.faq-editor__head h2 { margin: 0; font-size: 18px; font-weight: 700; color: var(--ui-ink); }
.faq-editor__x { border: 0; background: transparent; color: var(--ui-dim); font-size: 26px; line-height: 1; cursor: pointer; }
.faq-editor__body { flex: 1; overflow: auto; padding: 14px 18px; display: grid; gap: 8px; }
.faq-editor__label { font-size: 12px; letter-spacing: 0.05em; text-transform: uppercase; color: var(--ui-dim); }
.faq-editor__label-row { display: flex; align-items: center; justify-content: space-between; }
.faq-editor__link { border: 0; background: transparent; color: var(--ui-teal); font-size: 13px; font-weight: 600; cursor: pointer; }
.faq-editor__input { height: 42px; padding: 0 12px; border: 1px solid var(--ui-line-strong); border-radius: 8px; background: var(--ui-page); color: var(--ui-ink); font-size: 15px; font-weight: 600; }
.faq-editor__input:focus, .faq-editor__area:focus { outline: none; border-color: var(--ui-pri); }
.faq-editor__cols { display: grid; grid-template-columns: minmax(0, 1fr) minmax(0, 1fr); gap: 16px; margin-top: 6px; }
@media (max-width: 900px) { .faq-editor__cols { grid-template-columns: minmax(0, 1fr); } }
.faq-editor__write, .faq-editor__preview { display: flex; flex-direction: column; gap: 6px; min-width: 0; }
.faq-editor__tools { display: flex; flex-wrap: wrap; gap: 4px; }
.faq-editor__tools button { padding: 4px 9px; border: 1px solid var(--ui-line-strong); border-radius: 6px; background: var(--ui-card-2); color: var(--ui-ink); font-size: 12.5px; font-weight: 600; cursor: pointer; }
.faq-editor__tools button:hover { border-color: var(--ui-pri); }
.faq-editor__area { min-height: 420px; flex: 1; padding: 10px 12px; border: 1px solid var(--ui-line-strong); border-radius: 8px; background: var(--ui-page); color: var(--ui-ink); font-family: ui-monospace, Consolas, monospace; font-size: 13px; line-height: 1.55; resize: vertical; }
.faq-editor__help { display: grid; gap: 3px; padding: 10px 12px; border-radius: 8px; background: var(--ui-page); font-size: 12.5px; }
.faq-editor__help div { display: grid; grid-template-columns: 200px 1fr; gap: 8px; }
.faq-editor__help code { color: var(--ui-teal); background: transparent; padding: 0; }
.faq-editor__help span { color: var(--ui-dim); }
.faq-editor__paper { flex: 1; min-height: 420px; max-height: 62vh; overflow: auto; padding: 14px 16px; border: 1px solid var(--ui-line); border-radius: 8px; background: var(--ui-page); }
.faq-editor__pq { margin: 0 0 10px; font-size: 16.5px; font-weight: 700; color: var(--ui-ink); }
.faq-editor__foot { display: flex; align-items: center; gap: 8px; padding: 12px 18px; border-top: 1px solid var(--ui-line); }
.faq-editor__meta { flex: 1; font-size: 12.5px; color: var(--ui-dim); }
.faq-btn { padding: 8px 16px; border: 1px solid var(--ui-line-strong); border-radius: 8px; background: transparent; color: var(--ui-ink); font-size: 14px; font-weight: 600; cursor: pointer; }
.faq-btn--main { border-color: var(--ui-pri); background: var(--ui-pri); color: #ffffff; }
.faq-btn:disabled { opacity: 0.5; cursor: not-allowed; }
</style>
